import Modal from '../Modal';
import { useModal } from '../../../providers/ModalProvider';
import { modals } from '../../../providers/modals';
import { Text } from '../../typography';
import UserSelector from '../../UserSelector';
import { loader } from 'graphql.macro';
import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState, useMemo } from 'react';
import { useCurrentUser } from '../../../providers/UserProvider';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import styles from './CheckInModal.module.scss';
import Icon from '../../Icon';
import Button from '../../Button';
import { showToast } from '../../../graphql/cache/modal';
import { toastVariant } from '../../../constants/misc';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { isValidPhoneNumber } from 'react-phone-number-input';
import {
  DateTimePicker,
  LocalizationProvider,
  MobileDateTimePicker,
} from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useIsMobile } from '../../../hooks/misc';
import moment from 'moment';
import Dropdown from '../../dropdownV2/Dropdown';
import { useWorkspacePermissions } from '../../../providers/WorkspacePermissionsProvider';

const createCheckInMutation = loader('./CheckInModal.create.graphql');
const verifyPhoneMutation = loader('./CheckInModal.verify.graphql');
const lastCheckInQuery = loader('./CheckInModal.last.graphql');

export default function CheckInModal() {
  const isMobile = useIsMobile();
  const { user } = useCurrentUser();
  const { allWorkspaceUsers, companyAdmins } = useWorkspacePermissions();
  const { modalState, closeModal } = useModal();
  const [state, setState] = useState();
  const [verifying, setVerifying] = useState(false);
  const [supervisorInfo, setSupervisorInfo] = useState(false);

  const update = (variables) => setState((prev) => ({ ...prev, ...variables }));

  const [createCheckin] = useMutation(createCheckInMutation);
  const [verifyPhone] = useMutation(verifyPhoneMutation);

  const { show, intervalOptions, refetch } = modalState.checkInModal;

  const {
    data: { checkIns: [lastCheckIn] = [] } = {},
    loading: lastCheckInLoading,
  } = useQuery(lastCheckInQuery, {
    skip: !show,
    variables: { creatorId: `${user?.id}` },
  });

  const close = () => {
    closeModal({ modalName: modals.checkIn });
    setState(null);
    setVerifying(false);
  };

  const clearState = () => {
    setVerifying(false);
    setState({
      intervalValue: 30,
      intervalUnit: intervalOptions[0].value,
      user,
    });
  };

  useEffect(() => {
    if (!show || lastCheckInLoading) return;

    if (lastCheckIn) {
      setState({
        intervalValue: lastCheckIn.intervalValue ?? 30,
        intervalUnit: lastCheckIn.intervalUnit ?? intervalOptions[0].value,
        phone: lastCheckIn.phone ?? user?.phone,
        phoneVerified: lastCheckIn.phoneVerified ?? user?.phoneVerified,
        user,
        users: lastCheckIn.users?.length ? lastCheckIn.users : undefined,
        userResponsible: lastCheckIn.userResponsible ?? user?.supervisor,
        supervisor: lastCheckIn.directSupervisor ?? undefined,
        trip: !!(lastCheckIn.to || lastCheckIn.from),
        to: lastCheckIn.to ?? undefined,
        from: lastCheckIn.from ?? undefined,
        lastLocation: lastCheckIn.lastLocation ?? undefined,
      });
    } else {
      setState({
        intervalValue: 30,
        intervalUnit: intervalOptions[0].value,
        phone: user?.phone,
        phoneVerified: user?.phoneVerified,
        user,
        userResponsible: user?.supervisor,
      });
    }
    // eslint-disable-next-line
  }, [show, lastCheckIn, lastCheckInLoading]);

  const submitDisabled = useMemo(() => {
    const hasUser = state?.users?.length > 0;
    const hasUserResponsible = state?.userResponsible;
    const hasInterval = state?.intervalValue;
    const phoneVerified = state?.phoneVerified;
    const intervalIsValid =
      state?.intervalUnit === 'MINUTES' ? hasInterval > 4 : true;

    const isTrip = state?.trip;

    return isTrip
      ? !(
          hasUser &&
          hasUserResponsible &&
          hasInterval &&
          intervalIsValid &&
          phoneVerified &&
          state.to &&
          state.from
        )
      : !(
          hasUser &&
          hasUserResponsible &&
          hasInterval &&
          intervalIsValid &&
          phoneVerified
        );
  }, [state]);

  const DateTimePickerComponent = isMobile
    ? MobileDateTimePicker
    : DateTimePicker;

  const userOptions = useMemo(() => {
    const userMap = new Map();
    [...companyAdmins, ...allWorkspaceUsers].forEach((u) =>
      userMap.set(u.id, u),
    );
    return Array.from(userMap.values());
  }, [companyAdmins, allWorkspaceUsers]);

  return (
    <Modal
      submitText={'Start'}
      disableClickOutside={true}
      submitDisabled={submitDisabled}
      open={show}
      onClose={close}
      disableCloseOnSubmit={true}
      className={styles.modalWidth}
      footer={
        <Button
          outlined
          variant="secondary"
          icon="restart_alt"
          value="Clear"
          onClick={clearState}
        />
      }
      onSubmit={async () => {
        const { errors } = await createCheckin({
          variables: {
            ...state,
            ...(!!state?.trip
              ? {
                  to: state?.to,
                  from: state?.from,
                  estimatedArrival: state?.estimatedArrival,
                }
              : {}),
            userResponsibleId: state?.userResponsible?.id,
            supervisorId: state?.supervisor?.id,
            userIds: state?.users?.map(({ id }) => id),
          },
        });

        if (errors) {
          showToast({
            title: 'Error',
            message: 'There is already an active check in for this user',
            variant: toastVariant.warning,
          });
        } else {
          showToast({
            title: 'Success',
            message:
              'Lone Worker check in has started - you can view your history in the Check In tab. Stay Safe!',
            variant: toastVariant.success,
          });
          refetch();
          close();
        }
      }}
      title={'Create Check In'}
    >
      <div className={styles.container}>
        <Text noMargin weight="semiBold">
          User
        </Text>
        <Dropdown
          isMulti
          keepOptionsOpen
          type="user"
          options={userOptions}
          selected={state?.users}
          onChange={(newUsers) => update({ users: newUsers })}
        />
        <br />
        <Text noMargin weight="semiBold">
          Contact Number
        </Text>
        <div className={styles.phoneInput}>
          <PhoneInput
            defaultCountry="CA"
            placeholder="Enter Phone Number"
            value={isValidPhoneNumber(state?.phone ?? '') ? state?.phone : null}
            onChange={(v) => {
              setVerifying(false);
              update({ phone: v, phoneVerified: false, code: undefined });
            }}
          />
          <div className={styles.verifyContainer}>
            {state?.phoneVerified ? (
              <Icon className={styles.circleCheck}>check_circle_outline</Icon>
            ) : verifying ? (
              <div className={styles.verifyInput}>
                <Form.Control
                  placeholder="123456"
                  onChange={({ target: { value } }) => update({ code: value })}
                />
                <Button
                  outlined
                  variant="success"
                  size="sm"
                  value="Submit"
                  onClick={() =>
                    verifyPhone({
                      variables: {
                        userId: user.id,
                        code: state?.code,
                        phone: state?.phone,
                      },
                    })
                      .then((res) => {
                        const {
                          data: { verifyPhone: phoneVerified = false } = {},
                        } = res;
                        update({ phoneVerified, phone: state?.phone });
                        showToast({
                          title: 'Success',
                          message: 'Verified phone number',
                          variant: toastVariant.info,
                        });
                      })
                      .catch(() => {
                        showToast({
                          title: 'Error',
                          message: 'Failed to verify phone number',
                          variant: toastVariant.warning,
                        });
                      })
                      .finally(() => {
                        setVerifying(false);
                      })
                  }
                />
              </div>
            ) : (
              <Button
                outlined
                size="sm"
                value="Verify"
                onClick={() =>
                  verifyPhone({
                    variables: {
                      phone: state?.phone,
                    },
                  }).then(() => setVerifying(true))
                }
              />
            )}
          </div>
        </div>
        <br />
        <Text noMargin weight="semiBold">
          User Responsible
        </Text>
        <UserSelector
          cardProps={{ largeAvatar: true, weight: 'regular' }}
          usersToLeaveOut={state?.users?.length > 0 ? state?.users : []}
          selected={state?.userResponsible}
          onSelectionChanged={(u) => update({ userResponsible: u })}
          onClose={() => update({ userResponsible: null })}
        />
        <br />
        <div className={styles.flexRow}>
          <Text noMargin weight="semiBold">
            Direct Supervisor
          </Text>
          <Icon
            className={styles.infoIcon}
            onClick={() => setSupervisorInfo((prev) => !prev)}
          >
            info_outlined
          </Icon>
        </div>
        {supervisorInfo && (
          <Text noMargin size="sm" color="secondary">
            The direct supervisor will be notified if the user responsible fails
            to acknoweldge if a check in was missed
          </Text>
        )}
        <UserSelector
          cardProps={{ largeAvatar: true, weight: 'regular' }}
          usersToLeaveOut={state?.users?.length > 0 ? state?.users : []}
          selected={state?.supervisor}
          onSelectionChanged={(u) => update({ supervisor: u })}
          onClose={() => update({ supervisor: null })}
        />
        <br />
        <Text noMargin weight="semiBold">
          Check In Interval
        </Text>
        <div className={styles.intervalInput}>
          <Form.Control
            className={styles.intervalValue}
            type="number"
            isInvalid={
              state?.intervalUnit === 'MINUTES'
                ? state?.intervalValue < 5
                : true
            }
            min={state?.intervalUnit === 'MINUTES' ? 6 : 1}
            value={state?.intervalValue}
            onChange={({ target: { value } }) =>
              update({ intervalValue: parseInt(value) })
            }
          />
          <Select
            className={styles.intervalSelector}
            options={intervalOptions}
            defaultValue={intervalOptions[0]}
            placeholder={'Minutes'}
            isClearable
            value={intervalOptions.find(
              ({ value }) => value === state?.intervalUnit,
            )}
            onChange={(o) => update({ intervalUnit: o?.value })}
          />
        </div>
        {state?.intervalUnit === 'MINUTES' && state?.intervalValue < 6 && (
          <div className={styles.errorMsg}>
            <Text noMargin size="sm" color="red">
              Must be longer than 5 minutes
            </Text>
          </div>
        )}
        <br />
        <div>
          <div className={styles.locationHeader}>
            <Text noMargin weight="semiBold">
              Location
            </Text>
            <div className={styles.tripSwitch}>
              <Text noMargin weight="semiBold">
                Trip Check In
              </Text>
              <Form.Check
                type="switch"
                onChange={() => update({ trip: !!!state?.trip })}
              />
            </div>
          </div>
          {state?.trip ? (
            <div className={styles.tripInput}>
              <div>
                <Text noMargin weight="semiBold">
                  To
                </Text>
                <Form.Control
                  value={state?.to}
                  onChange={({ target: { value } }) => update({ to: value })}
                />
              </div>
              <div>
                <Text noMargin weight="semiBold">
                  From
                </Text>
                <Form.Control
                  value={state?.from}
                  onChange={({ target: { value } }) => update({ from: value })}
                />
              </div>
              <div>
                <Text noMargin weight="semiBold">
                  Estimated Arrival (optional)
                </Text>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DateTimePickerComponent
                    className={styles.datepicker}
                    onChange={(newMoment) => {
                      if (!newMoment || !newMoment.isValid()) {
                        return;
                      }
                      update({
                        estimatedArrival: `${newMoment.valueOf()}`,
                      });
                    }}
                    value={
                      moment(parseInt(state?.estimatedArrival)).isValid()
                        ? moment(parseInt(state?.estimatedArrival))
                        : null
                    }
                  />
                </LocalizationProvider>
              </div>
            </div>
          ) : (
            <div className={styles.locationInput}>
              <Form.Control
                value={state?.lastLocation}
                onChange={({ target: { value } }) =>
                  update({ lastLocation: value })
                }
              />
              <Icon
                className={styles.locationIcon}
                onClick={() => {
                  navigator.geolocation.getCurrentPosition(
                    (loc) => {
                      const lat = loc.coords.latitude.toFixed(4);
                      const lon = loc.coords.longitude.toFixed(4);
                      update({
                        lastLocation: `lat: ${lat}, long: ${lon}`,
                      });
                      return;
                    },
                    (err) => console.error({ err }),
                  );
                }}
              >
                add_location_alt
              </Icon>
            </div>
          )}
        </div>
      </div>
      <br />
    </Modal>
  );
}
